import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Typography,
  createTheme,
  Box,
  useMediaQuery,
} from "@mui/material";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});

const createMicrophonePermissionDeniedError = () =>
  Object.assign(new Error("Microphone permission denied"), {
    name: "NotAllowedError",
  });

const MicAccessPopup = ({
  enabled = true,
  onPermissionDenied,
  onPermissionGranted,
  onDismiss,
  showDeniedInstructions = false,
}) => {
  const [open, setOpen] = useState(false);
  const [permissionState, setPermissionState] = useState("prompt");
  const isPhone = useMediaQuery("(max-width:600px)");
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const dialogWidth = isPhone
    ? isLandscape
      ? "76vw"
      : "92vw"
    : "400px";
  const dialogMargin = isPhone ? "12px auto" : "150px";


  // Check microphone permission only after the viewer flow asks for audio.
  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      setPermissionState("prompt");
      return undefined;
    }

    let cancelled = false;
    let permissionStatus = null;

    const checkPermission = async () => {
      if (navigator.permissions) {
        try {
          permissionStatus = await navigator.permissions.query({
            name: "microphone",
          });

          if (cancelled) {
            return;
          }

          const handlePermissionStateChange = () => {
            if (cancelled) {
              return;
            }

            if (permissionStatus.state === "granted") {
              setOpen(false);
              onPermissionGranted?.();
              return;
            }

            if (permissionStatus.state === "denied") {
              setPermissionState("denied");
              setOpen(showDeniedInstructions);
              onPermissionDenied?.(createMicrophonePermissionDeniedError(), {
                keepPromptOpen: showDeniedInstructions,
              });
              return;
            }

            setPermissionState("prompt");
            setOpen(true);
          };

          handlePermissionStateChange();
          permissionStatus.onchange = handlePermissionStateChange;
        } catch (error) {
          console.error("Error checking microphone permission:", error);
          if (!cancelled) {
            setOpen(true);
          }
        }
      } else {
        if (!cancelled) {
          setOpen(true);
        }
      }
    };

    checkPermission();

    return () => {
      cancelled = true;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [
    enabled,
    onPermissionDenied,
    onPermissionGranted,
    showDeniedInstructions,
  ]);

  // Function to request microphone access
  const handleRequestMicAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream?.getTracks?.().forEach((track) => track.stop());
      setOpen(false);
      setPermissionState("prompt");
      onPermissionGranted?.();
    } catch (error) {
      setPermissionState("denied");
      setOpen(showDeniedInstructions);
      onPermissionDenied?.(error, {
        keepPromptOpen: showDeniedInstructions,
      });
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    onDismiss?.();
  };

  const permissionDenied = permissionState === "denied";

  return (
    <ThemeProvider theme={darkTheme}>
      <Dialog
        open={open}
        onClose={handleDismiss}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-container": {
            alignItems: isPhone && isLandscape ? "flex-start" : "center", // Positions the dialog closer to the bottom
            padding: isPhone ? "12px" : undefined, // keep desktop default spacing
          },
        }}
        PaperProps={{
          sx: isPhone
            ? {
                width: dialogWidth,
                maxWidth: dialogWidth,
                margin: dialogMargin,
                borderRadius: 2,
                px: "var(--dialog-padding, 24px)",
                py: 2,
              }
            : {
                width: "400px",
                maxWidth: "400px",
                margin: "150px",
              },
        }}
      >
        <DialogTitle>
          {permissionDenied
            ? "Microphone access is off"
            : "Turn on mic to use 10k Science"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {permissionDenied
              ? "Allow microphone access for this site in your browser settings, then try again."
              : "You will be able to answer questions and get feedback during your experience."}
          </Typography>
          <Box
            mt={2}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleRequestMicAccess}
            >
              <span className="control-label">
                {permissionDenied ? "Try microphone again" : "Turn on microphone"}
              </span>
            </Button>
            {permissionDenied && (
              <Button variant="text" color="inherit" onClick={handleDismiss}>
                <span className="control-label">Not now</span>
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>

     
    </ThemeProvider>
  );
};

export default MicAccessPopup;
